'use client'

import * as React from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useRouter, usePathname } from 'next/navigation'

import TabGroup from '@/app/components/ui/tabgroup'
import { Strings } from '@/lib/constants/strings'
import { AppRoutes } from '@/lib/constants/routes'
import { AriaAttributes } from '@/lib/constants/ariaattributes'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import { useMLSSystemsProvider } from '../hooks/useMLSSystems'

export default function RecruitingTabGroup() {
	const router = useRouter()
	const pathname = usePathname()
	const theme = useTheme()

	const { mlsSystems, isLoading } = useMLSSystemsProvider()
	const hasTabDisabled = !isLoading && mlsSystems.length === 0
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

	const pipelineTabLabel = isMobile
		? Strings.TAB_PROSPECT_PIPELINE_M
		: Strings.TAB_PROSPECT_PIPELINE
	const nurtureTabLabel = isMobile
		? Strings.TAB_NURTURING_M
		: Strings.TAB_NURTURING
	const mlsSearchTabLabel = isMobile
		? Strings.TAB_SEARCH
		: Strings.TAB_MLS_SEARCH

	const links = React.useMemo(() => {
		return [
			{
				label: pipelineTabLabel,
				href: AppRoutes.RECRUITING_PROSPECT_PIPELINE,
			},
			{
				label: nurtureTabLabel,
				href: AppRoutes.RECRUITING_NURTURING,
			},
			{
				label: mlsSearchTabLabel,
				href: AppRoutes.RECRUITING_MLS_SEARCH,
			},
		]
	}, [])

	const activeTabIndex = React.useMemo(() => {
		let index = 0
		index = links.findIndex(link => pathname.startsWith(link.href))
		if (
			pathname.endsWith('/mlssearch') ||
			pathname.endsWith('/nurturing') ||
			pathname.endsWith('/pipeline')
		) {
			return index // If on agent profile, no active tab
		}
		return -1
	}, [pathname, links])

	const handleAnalytics = (index: number) => {
		const tabEventData = [
			{
				tab_name: 'Pipeline',
			},
			{
				tab_name: 'Nurture',
			},

			{
				tab_name: 'MLS Search',
			},
		]

		pushToDataLayer({
			event: 'recruiting_tab',
			...tabEventData[index],
		})
	}

	const handleTabClick = (index: number) => {
		const { href } = links[index]
		if (href !== pathname) {
			router.push(href)
		}
		handleAnalytics(index)
	}
	return (
		<Box
			sx={{
				width: '100%',
				backgroundColor: theme.palette.background.paper,
				position: 'relative',
				zIndex: 1,
			}}
		>
			<Box
				sx={{
					borderTop: `1px solid ${theme.palette.stroke.neutral.low}`,
					borderBottom: `1px solid ${theme.palette.stroke.neutral.low}`,
					fontSize: '16px',
				}}
			>
				<TabGroup
				    evenlyDistributedOnMobile
					className="core-ui-tabs"
					ariaLabel={AriaAttributes.RECRUITING_TABS}
					links={links}
					activeTabIndex={activeTabIndex}
					onTabClick={handleTabClick}
					sxStyles={{
						pl: { xs: 0, sm: '48px' },
						bgcolor: theme.palette.surface.neutral.t1Base,
						pr: { xs: 0, sm: 'inherit' },
						pointerEvents: hasTabDisabled ? 'none' : 'auto',
						opacity: hasTabDisabled ? 0.5 : 1,
					}}
				/>
			</Box>
		</Box>
	)
}
